import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';

interface ImageItem {
  id: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  folderId: string;
  createdAt: string;
  createdBy: string;
  size?: number;
  type?: string;
}

interface ImageEditModalProps {
  open: boolean;
  onClose: () => void;
  image: ImageItem | null;
  onSave: (imageId: string, newName: string) => Promise<void>;
  existingImages: ImageItem[];
}

const ImageEditModal: React.FC<ImageEditModalProps> = ({
  open,
  onClose,
  image,
  onSave,
  existingImages
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [imageName, setImageName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal açıldığında resim adını set et
  useEffect(() => {
    if (image && open) {
      setImageName(image.name);
      setError(null);
    }
  }, [image, open]);

  // Modal kapanırken temizle
  const handleClose = () => {
    if (isUpdating) return;
    setImageName('');
    setError(null);
    onClose();
  };

  // Resim güncelle
  const handleSave = async () => {
    if (!image || !imageName.trim()) {
      setError(tt('Resim adı boş olamaz', 'Image name cannot be empty'));
      return;
    }

    // Aynı klasörde aynı isimde resim var mı kontrol et (kendisi hariç)
    const existingImage = existingImages.find(img => 
      img.id !== image.id && 
      img.folderId === image.folderId && 
      img.name.toLowerCase() === imageName.trim().toLowerCase()
    );
    
    if (existingImage) {
      setError(tt('Bu klasörde aynı isimde bir resim zaten mevcut', 'An image with the same name already exists in this folder'));
      return;
    }

    // İsim değişmemişse
    if (imageName.trim() === image.name) {
      handleClose();
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      await onSave(image.id, imageName.trim());
      
      // State güncellemesinin tamamlanması için kısa bir gecikme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      handleClose();
    } catch (error: any) {
      console.error('Resim güncelleme hatası:', error);
      // Backend'den gelen hata mesajını göster
      const errorMessage = error?.message || tt('Resim güncellenirken bir hata oluştu', 'An error occurred while updating image');
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Enter tuşu ile kaydet
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isUpdating) {
      handleSave();
    }
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!image) return null;

  return (
    <Dialog
      open={open}
      onClose={!isUpdating ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <EditIcon sx={{ mr: 1 }} />
        {tt('Resim Adını Düzenle', 'Edit Image Name')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={image.url}
            alt={image.name}
            sx={{
              width: 60,
              height: 60,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              mr: 2
            }}
          />
          <Box>
            <Typography variant="body2" color="text.secondary">
              {tt('Düzenlenen resim:', 'Editing image:')} <strong>{image.name}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {image.size ? formatFileSize(image.size) : tt('Bilinmiyor', 'Unknown')} • {new Date(image.createdAt).toLocaleDateString(localeCode)}
            </Typography>
          </Box>
        </Box>

        <TextField
          autoFocus
          margin="dense"
          label={tt('Yeni Resim Adı', 'New Image Name')}
          type="text"
          fullWidth
          variant="outlined"
          value={imageName}
          onChange={(e) => setImageName(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isUpdating}
          placeholder={tt('Yeni resim adını giriniz...', 'Enter new image name...')}
          helperText={tt('Aynı klasörde resim adları benzersiz olmalıdır', 'Image names must be unique in the same folder')}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={isUpdating}
        >
          {tt('İptal', 'Cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          color="primary" 
          disabled={isUpdating || !imageName.trim()}
          startIcon={isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {isUpdating ? tt('Güncelleniyor...', 'Updating...') : tt('Kaydet', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageEditModal; 